import { useState, useCallback } from 'react';
import { NotificationService } from '../service/notificationService';

/**
 * Custom hook for managing SMS notifications related to invoices
 * 
 * This hook handles sending SMS notifications for invoice-related events
 * such as payment confirmations, reminders, and status updates.
 * Uses the NotificationService for sending text messages.
 * 
 * @returns {Object} SMS sending functions and state
 */
export const useInvoiceSMS = () => {
    const [isSending, setIsSending] = useState(false);
    const [smsError, setSmsError] = useState(null);
    const [lastSent, setLastSent] = useState(null);

    /**
     * Send custom SMS message
     */
    const sendSMS = useCallback(async (message, customer, invoiceId, type = 'custom') => {
        setIsSending(true);
        setSmsError(null);

        try {
            const phoneNumber = customer.mobile;
            if (!phoneNumber) {
                throw new Error('Customer mobile number not found');
            }
            const response = await NotificationService.sendTextNotification({
                phone: phoneNumber,
                message: message,
                customerId: customer.id,
                invoiceId: invoiceId
            });

            setLastSent({
                type,
                phoneNumber,
                timestamp: new Date()
            });

            return response;
        } catch (error) {
            console.error('Error sending custom SMS:', error);
            setSmsError('Failed to send SMS');
            return { success: false, error };
        } finally {
            setIsSending(false);
        }
    }, []);


    return {
        // State
        isSending,
        smsError,
        lastSent,

        // Actions
        sendSMS,
    };
};

export default useInvoiceSMS;
